import { rgba } from 'polished';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import {
	ArrowRightIcon,
	Avatar,
	Button,
	CheckmarkIcon,
	CloseIcon,
	tokens,
} from '@treams/treams-ui';

export type TalentReviewPlacementPosition = {
	label: string;
	color: number;
};

const toHex = (color: number) => `#${color.toString(16).padStart(6, '0')}`;

const ResultTitle = styled.div`
	margin-top: ${tokens.gap.group}px;
	color: ${tokens.text.secondary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	font-weight: ${tokens.typography.weights.medium};
	line-height: ${tokens.typography.lineHeights.body};
`;

const ResultRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: ${tokens.gap.group}px;
	margin-top: ${tokens.gap.group}px;
`;

const Position = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: ${tokens.gap.element}px;
	min-width: 0;
`;

const PositionChip = styled.div<{ $color?: string }>`
	padding: ${tokens.gap.inline}px ${tokens.gap.element}px;
	background: ${tokens.surface.white};
	border-radius: ${tokens.radius.sm}px;
	color: ${(props) => props.$color ?? tokens.text.secondary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	font-weight: ${tokens.typography.weights.medium};
	line-height: ${tokens.typography.lineHeights.body};
	white-space: nowrap;
`;

const SuggestedCard = styled.div<{ $color: string }>`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: ${tokens.gap.element}px;
	min-width: 140px;
	padding: ${tokens.gap.group}px ${tokens.gap.section}px;
	background: ${(props) => rgba(props.$color, 0.1)};
	border: 1.5px solid ${(props) => props.$color};
	border-radius: ${tokens.radius.sm}px;
	box-sizing: border-box;
`;

const ResultActions = styled.div`
	display: flex;
	align-items: stretch;
	gap: ${tokens.gap.element}px;
	margin-top: ${tokens.gap.section}px;

	> * {
		flex: 1;
	}
`;

const ResultArrow = styled.div`
	display: flex;
	align-items: center;
	color: ${tokens.icon.secondary};
	flex-shrink: 0;

	svg {
		display: block;
		width: 20px;
		height: 20px;
	}
`;

export default function TalentReviewPlacementHelpResult(props: {
	user: Pick<graphql.User, 'firstName' | 'lastName'> & {
		avatar?: graphql.User['avatar'];
	};
	current?: TalentReviewPlacementPosition;
	suggested: TalentReviewPlacementPosition;
	onKeep: () => void;
	onMove?: () => void;
}) {
	const { t } = useTranslation();
	const currentColor = props.current ? toHex(props.current.color) : undefined;
	const suggestedColor = toHex(props.suggested.color);

	return (
		<>
			<ResultTitle>{t('talentReviews.v2.placementHelp.result.title')}</ResultTitle>
			<ResultRow>
				<Position>
					<Avatar size={32} user={props.user} />
					<PositionChip $color={currentColor}>
						{props.current?.label || t('talentReviews.v2.placementHelp.result.notPlaced')}
					</PositionChip>
				</Position>
				<ResultArrow>
					<ArrowRightIcon />
				</ResultArrow>
				<SuggestedCard $color={suggestedColor}>
					<Avatar size={48} user={props.user} />
					<PositionChip $color={suggestedColor}>{props.suggested.label}</PositionChip>
				</SuggestedCard>
			</ResultRow>
			<ResultActions>
				<Button
					type="secondary"
					icon={<CloseIcon />}
					title={t('talentReviews.v2.placementHelp.result.keep')}
					onClick={props.onKeep}
				/>
				<Button
					type="primary"
					icon={<CheckmarkIcon />}
					title={t('talentReviews.v2.placementHelp.result.move', {
						label: props.suggested.label,
					})}
					disabled={!props.onMove}
					onClick={props.onMove}
				/>
			</ResultActions>
		</>
	);
}
